/* eslint-disable react/jsx-max-depth */
import { useCallback, useEffect, useState } from 'react';
import { getIn, useFormikContext } from 'formik';

import type { ContentOriginId, Id } from '@e-socna/data-layer';

import Grid from '@mui/material/Grid';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import FormHelperText from '@mui/material/FormHelperText';
import type { GridRowSelectionModel } from '@mui/x-data-grid';

import { EMPTY_ARRAY } from '@e-socna/empty';

import { ResolvableServiceList } from '../../../components/ResolvableServiceList';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useFindOneResolvableServiceQuery } from '../../../pages/ResolvableService/hooks/useFindOneResolvableServiceQuery.hook';
import { Loading } from '../../../components/Loading';
import { useFormikOnNextTick } from '../../../hooks/useFormikOnNextTick.hook';

import { useInitialValuesFactory as useInitialResolvableServiceValuesFactory } from '../../ResolvableService/hooks/useInitialValuesFactory.hook';

import type { ResolvableServiceUsageForm_ResolvableServiceUsage } from '../fragments/resolvable-service-usage.fragment';

interface Props {
  readonly excludedIds?: readonly Id[];
  readonly id: string;
  readonly setIsUsingResolvableServiceSteps: (value: boolean) => void;
}

// The data grid needs a bounded height to scroll its rows instead of growing with
// them: the step lays out as a full-height column whose last cell (the list) takes
// the remaining space.
const STEP_GRID_SX = {
  alignContent: 'flex-start',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  height: '100%',
};
const SERVICE_LIST_CELL_SX = {
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  minHeight: 300,
  pt: 1,
  pb: 1,
};
const SERVICE_LIST_SX = { flexGrow: 1, minHeight: 0, width: '100%', pt: 2 };

export const ResolvableServicesSelectionFormStep = ({
  excludedIds,
  setIsUsingResolvableServiceSteps,
}: Readonly<Props>) => {
  const [didMount, setDidMount] = useState(false);
  const [serviceToFetchId, setServiceToFetchId] = useState<Id | undefined>();
  const [mustFetchService, setMustFetchService] = useState(false);

  const {
    errors,
    handleChange,
    handleBlur,
    setFieldValue,
    setValues,
    touched,
    values,
  } = useFormikContext<ResolvableServiceUsageForm_ResolvableServiceUsage>();

  const { setFieldValue: setFieldValueOnNextTick } = useFormikOnNextTick();

  const { contentOriginsCodeMap } = useReferencesContext();

  const handleRowSelectionModelChange = useCallback(
    (selection: Readonly<GridRowSelectionModel>) => {
      const newResolvableServiceId = [...selection.ids].pop() as Id | undefined;

      void setFieldValue('serviceId', newResolvableServiceId);

      if (
        values.serviceOriginId !== contentOriginsCodeMap.Modified._id ||
        newResolvableServiceId === serviceToFetchId
      ) {
        return;
      }

      setMustFetchService(true);
      setServiceToFetchId(newResolvableServiceId);
    },
    [
      setFieldValue,
      contentOriginsCodeMap.Modified._id,
      serviceToFetchId,
      values.serviceOriginId,
    ],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDidMount(true);
  }, []);

  const {
    data: resolvableServiceData,
    isLoading: resolvableServiceQueryIsLoading,
  } = useFindOneResolvableServiceQuery(
    serviceToFetchId,
    serviceToFetchId && didMount && mustFetchService,
  );

  useEffect(() => {
    if (
      !didMount ||
      !resolvableServiceData?.findOneResolvableService._id ||
      !mustFetchService
    ) {
      return;
    }

    setFieldValueOnNextTick(
      'specialService',
      resolvableServiceData.findOneResolvableService,
    );

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMustFetchService(false);
    // MHO : we don't want to run this effect once component is mounted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    contentOriginsCodeMap.Modified._id,
    // didMount,
    mustFetchService,
    setFieldValueOnNextTick,
    setFieldValue,
    resolvableServiceData?.findOneResolvableService._id,
    values.serviceOriginId,
  ]);

  const { initialValues: initialResolvableServiceValues } =
    useInitialResolvableServiceValuesFactory();

  useEffect(() => {
    if (!didMount) {
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setServiceToFetchId(undefined);
    setMustFetchService(false);

    switch (values.serviceOriginId) {
      case contentOriginsCodeMap.Catalog._id: {
        setIsUsingResolvableServiceSteps(false);
        void setValues((prevValues) => ({
          ...prevValues,
          serviceId: null,
          specialService: null,
        }));
        return;
      }
      case contentOriginsCodeMap.Special._id: {
        setIsUsingResolvableServiceSteps(true);
        void setValues((prevValues) => ({
          ...prevValues,
          serviceId: null,
          specialService: {
            ...initialResolvableServiceValues,
            __typename: 'ResolvableServiceBase' as const,
          },
        }));
        setMustFetchService(true);
        return;
      }
      case contentOriginsCodeMap.Modified._id: {
        setIsUsingResolvableServiceSteps(true);
        void setValues((prevValues) => ({
          ...prevValues,
          serviceId: null,
          specialService: null,
        }));
        return;
      }
      case null:
      default: {
        throw new Error('Unhandled service origin');
      }
    }
    // MHO : we don't want to run this effect once component is mounted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    contentOriginsCodeMap.Catalog._id,
    contentOriginsCodeMap.Modified._id,
    contentOriginsCodeMap.Special._id,
    // didMount,
    values.serviceOriginId,
    initialResolvableServiceValues,
    setFieldValueOnNextTick,
    setIsUsingResolvableServiceSteps,
  ]);

  const showServiceList = [
    contentOriginsCodeMap.Catalog._id,
    contentOriginsCodeMap.Modified._id,
  ].includes(values.serviceOriginId as ContentOriginId);

  return (
    <>
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
      {!!resolvableServiceQueryIsLoading && <Loading />}
      <Grid container spacing={2} sx={STEP_GRID_SX}>
        <Grid size={12} sx={{ pt: 1, pb: 1 }}>
          <FormControl
            error={!!(errors.serviceOriginId && touched.serviceOriginId)}
          >
            <FormLabel
              error={!!(errors.serviceOriginId && touched.serviceOriginId)}
              id="service-origin-radio-buttons-group-label"
            >
              Source du Service *
            </FormLabel>
            <RadioGroup
              aria-labelledby="service-origin-radio-buttons-group-label"
              name="serviceOriginId"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.serviceOriginId}
            >
              <FormControlLabel
                control={<Radio />}
                label="Service spécialement défini dans le cadre de la mission"
                value={contentOriginsCodeMap.Special._id}
              />
              <FormControlLabel
                control={<Radio />}
                label="Service issu du référentiel"
                value={contentOriginsCodeMap.Catalog._id}
              />
              <FormControlLabel
                control={<Radio />}
                label="Service issu du référentiel mais modifié pour la mission"
                value={contentOriginsCodeMap.Modified._id}
              />
            </RadioGroup>
          </FormControl>
          {!!(errors.serviceOriginId && touched.serviceOriginId) && (
            <FormHelperText>{getIn(errors, 'serviceOriginId')}</FormHelperText>
          )}
        </Grid>
        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
        {!!showServiceList && (
          <Grid size={12} sx={SERVICE_LIST_CELL_SX}>
            <FormLabel id="service-origin-radio-buttons-group-label">
              Services du Référentiel *
            </FormLabel>
            <ResolvableServiceList
              checkboxSelection
              excludedIds={excludedIds}
              onRowSelectionModelChange={handleRowSelectionModelChange}
              rowSelectionModel={{
                type: 'include',
                ids: new Set(
                  values.serviceId ? [values.serviceId] : EMPTY_ARRAY,
                ),
              }}
              sx={SERVICE_LIST_SX}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};
